import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Badge } from 'react-bootstrap';
import authService from '../services/authService';
import BackToDashboard from '../components/BackToDashboard';

function BrandResults() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [archetypeData, setArchetypeData] = useState(null);

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Check if data was passed via navigation state
    if (location.state && location.state.archetypeData) {
      setArchetypeData(location.state.archetypeData);
    } else {
      // Fetch from API if not in navigation state
      await fetchArchetypes();
    }
  };

  const fetchArchetypes = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/brand/get-archetypes', {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setArchetypeData(data);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'No archetype data found. Please retake the quiz.');
      }
    } catch (err) {
      setError('Error loading archetypes: ' + err.message);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <p>Loading...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8}>
            <Alert variant="danger">{error}</Alert>
            <Button variant="primary" onClick={() => navigate('/dashboard/brand-quiz-intro')}>
              Retake Quiz
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  if (!archetypeData) {
    return (
      <Container className="py-5 text-center">
        <p>Loading archetype data...</p>
      </Container>
    );
  }

  const { primary, secondary, tertiary } = archetypeData;

  // Archetype descriptions
  const archetypeInfo = {
    'Innocent': {
      tagline: 'Purity, Optimism, Simplicity',
      description: 'The Innocent brand is pure, optimistic, and wholesome. Think Dove, Disney, Whole Foods.',
      traits: ['Honest', 'Pure', 'Optimistic', 'Simple', 'Traditional', 'Loyal'],
      color: 'primary'
    },
    'Sage': {
      tagline: 'Wisdom, Knowledge, Truth',
      description: 'The Sage brand seeks and shares knowledge. Think Google, TED, Harvard.',
      traits: ['Wise', 'Knowledgeable', 'Analytical', 'Thoughtful', 'Mentor-like', 'Expert'],
      color: 'info'
    },
    'Explorer': {
      tagline: 'Freedom, Discovery, Adventure',
      description: 'The Explorer brand values freedom and new experiences. Think Patagonia, REI, Jeep.',
      traits: ['Adventurous', 'Independent', 'Pioneering', 'Bold', 'Free-spirited', 'Authentic'],
      color: 'success'
    },
    'Outlaw': {
      tagline: 'Rebellion, Disruption, Revolution',
      description: 'The Outlaw brand breaks rules and challenges the status quo. Think Harley-Davidson, Virgin, Apple.',
      traits: ['Rebellious', 'Disruptive', 'Revolutionary', 'Bold', 'Edgy', 'Liberated'],
      color: 'danger'
    },
    'Magician': {
      tagline: 'Transformation, Vision, Innovation',
      description: 'The Magician brand creates transformative experiences. Think Disney, Tesla, Apple.',
      traits: ['Visionary', 'Charismatic', 'Imaginative', 'Transformative', 'Inspiring', 'Innovative'],
      color: 'warning'
    },
    'Hero': {
      tagline: 'Courage, Achievement, Strength',
      description: 'The Hero brand helps people overcome challenges and achieve greatness. Think Nike, FedEx, U.S. Army.',
      traits: ['Courageous', 'Strong', 'Determined', 'Confident', 'Motivating', 'Action-oriented'],
      color: 'danger'
    },
    'Lover': {
      tagline: 'Passion, Intimacy, Beauty',
      description: 'The Lover brand creates intimate, passionate experiences. Think Chanel, Godiva, Victoria\'s Secret.',
      traits: ['Passionate', 'Sensual', 'Intimate', 'Warm', 'Romantic', 'Indulgent'],
      color: 'danger'
    },
    'Jester': {
      tagline: 'Joy, Fun, Playfulness',
      description: 'The Jester brand brings joy and laughter. Think M&Ms, Old Spice, Ben & Jerry\'s.',
      traits: ['Playful', 'Fun', 'Humorous', 'Irreverent', 'Spontaneous', 'Light-hearted'],
      color: 'warning'
    },
    'Everyman': {
      tagline: 'Belonging, Authenticity, Connection',
      description: 'The Everyman brand is down-to-earth and relatable. Think IKEA, Target, eBay.',
      traits: ['Authentic', 'Down-to-earth', 'Supportive', 'Friendly', 'Relatable', 'Democratic'],
      color: 'secondary'
    },
    'Caregiver': {
      tagline: 'Compassion, Nurturing, Service',
      description: 'The Caregiver brand nurtures and protects. Think Johnson & Johnson, UNICEF, Campbell\'s.',
      traits: ['Compassionate', 'Nurturing', 'Generous', 'Protective', 'Warm', 'Selfless'],
      color: 'success'
    },
    'Ruler': {
      tagline: 'Control, Leadership, Authority',
      description: 'The Ruler brand represents leadership and control. Think Mercedes-Benz, Rolex, Microsoft.',
      traits: ['Powerful', 'Authoritative', 'Confident', 'Organized', 'Responsible', 'Elite'],
      color: 'dark'
    },
    'Creator': {
      tagline: 'Creativity, Innovation, Expression',
      description: 'The Creator brand values imagination and self-expression. Think Lego, Adobe, Crayola.',
      traits: ['Creative', 'Imaginative', 'Innovative', 'Artistic', 'Expressive', 'Entrepreneurial'],
      color: 'info'
    }
  };

  const primaryInfo = archetypeInfo[primary.name] || {};
  const secondaryInfo = secondary ? archetypeInfo[secondary.name] : null;
  const tertiaryInfo = tertiary ? archetypeInfo[tertiary.name] : null;

  return (
    <Container className="py-5">
      <BackToDashboard module="branding" />
      <Row className="justify-content-center">
        <Col md={12} lg={10}>
          <Card className="shadow">
            <Card.Body className="p-5">
              <h1 className="text-center mb-4">🎨 Your Brand Archetype</h1>

              <Alert variant="success" className="text-center">
                <strong>Quiz Complete!</strong> Your brand archetype has been calculated based on your responses.
              </Alert>

              {/* Primary Archetype */}
              <Card className="mb-4 border-primary">
                <Card.Header className="bg-primary text-white">
                  <h4 className="mb-0">Primary Archetype: {primary.name}</h4>
                  <small>{primaryInfo.tagline}</small>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={8}>
                      <p className="lead mb-3">{primaryInfo.description}</p>
                      <p className="mb-2"><strong>Score:</strong> {primary.score.toFixed(1)} / 12.0</p>
                    </Col>
                    <Col md={4}>
                      <h6>Key Traits:</h6>
                      <div className="d-flex flex-wrap gap-2">
                        {primaryInfo.traits && primaryInfo.traits.map((trait, idx) => (
                          <Badge key={idx} bg={primaryInfo.color} className="px-2 py-1">
                            {trait}
                          </Badge>
                        ))}
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Secondary Archetype */}
              {secondary && secondaryInfo && (
                <Card className="mb-4 border-secondary">
                  <Card.Header className="bg-secondary text-white">
                    <h5 className="mb-0">Secondary Archetype (Flavor): {secondary.name}</h5>
                    <small>{secondaryInfo.tagline}</small>
                  </Card.Header>
                  <Card.Body>
                    <p>{secondaryInfo.description}</p>
                    <p className="mb-0"><strong>Score:</strong> {secondary.score.toFixed(1)} / 12.0</p>
                    <small className="text-muted">
                      Your secondary archetype adds nuance to your brand personality.
                    </small>
                  </Card.Body>
                </Card>
              )}

              {/* Tertiary Archetype */}
              {tertiary && tertiaryInfo && (
                <Card className="mb-4 border-info">
                  <Card.Header className="bg-info text-white">
                    <h6 className="mb-0">Tertiary Archetype (Accent): {tertiary.name}</h6>
                    <small>{tertiaryInfo.tagline}</small>
                  </Card.Header>
                  <Card.Body>
                    <p>{tertiaryInfo.description}</p>
                    <p className="mb-0"><strong>Score:</strong> {tertiary.score.toFixed(1)} / 12.0</p>
                    <small className="text-muted">
                      Your tertiary archetype provides subtle influence on your brand expression.
                    </small>
                  </Card.Body>
                </Card>
              )}

              {/* All Scores */}
              <Card className="mb-4">
                <Card.Header>
                  <strong>All Archetype Scores</strong>
                </Card.Header>
                <Card.Body>
                  <Row>
                    {archetypeData.all_scores && Object.entries(archetypeData.all_scores)
                      .sort((a, b) => b[1] - a[1])
                      .map(([name, score]) => (
                        <Col md={6} lg={4} key={name} className="mb-2">
                          <div className="d-flex justify-content-between align-items-center">
                            <span>{name}:</span>
                            <Badge bg="secondary">{score.toFixed(1)}</Badge>
                          </div>
                        </Col>
                      ))
                    }
                  </Row>
                </Card.Body>
              </Card>

              {/* Next Steps */}
              <Alert variant="info">
                <h5>What's Next?</h5>
                <p className="mb-0">
                  Your brand archetype will be used to generate a custom color palette, typography recommendations,
                  logo direction, iconography style, and visual mood board. These elements will ensure all your
                  marketing materials have a cohesive, professional look that resonates with your target audience.
                </p>
              </Alert>

              <div className="d-flex justify-content-between mt-4">
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate('/dashboard/brand-quiz')}
                >
                  ← Retake Quiz
                </Button>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => navigate('/dashboard/color-palettes')}
                >
                  Choose Color Palette →
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default BrandResults;
